"use client"

import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Formik, Form, Field, ErrorMessage } from "formik"
import * as Yup from "yup"
import { showSuccessToast, showErrorToast } from "./Toast"
import Loader from "./Loader"
import { api, getAuthHeaders } from "../utils/api"
import "../styles/subscription.scss"
import { Circle, CircleCheck, Eye, PlusCircleIcon } from "lucide-react"
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"

// demo invoices (optional)
const invoicesData = [
  { id: "MW-0124", date: "12/07/2025", plan: "Basic", amount: "$49", status: "Pending" },
  { id: "MW-3645", date: "10/01/2025", plan: "Standard", amount: "$89", status: "Completed" },
  { id: "MW-4361", date: "24/06/2024", plan: "Premium", amount: "$149", status: "Completed" },
]

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_your_key_here")

/* ========== Add Card Form with Stripe ========== */
const AddCardForm = ({ onSuccess, onCancel, loading, setLoading }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState(null)
  const [nameOnCard, setNameOnCard] = useState("")

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!stripe || !elements) {
      setError("Stripe not loaded")
      return
    }

    if (!nameOnCard || nameOnCard.trim().length < 3) {
      setError("Please enter the name on card")
      return
    }

    const card = elements.getElement(CardElement)
    if (!card) {
      setError("Card element not found")
      return
    }

    setLoading(true)
    try {
      const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
        type: "card",
        card: card,
        billing_details: {
          name: nameOnCard,
        },
      })

      if (stripeError) {
        setError(stripeError.message)
        setLoading(false)
        return
      }

      if (paymentMethod) {
        onSuccess(paymentMethod)
      }
    } catch (err) {
      console.error("Error creating payment method:", err)
      setError(err.message || "Failed to create payment method")
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="add-card-form">
      <div className="form-group">
        <label className="form-label text-muted">Card Name</label>
        <input
          type="text"
          value={nameOnCard}
          onChange={(e) => setNameOnCard(e.target.value)}
          placeholder="John Doe"
          className="form-control"
          disabled={loading}
          autoComplete="off"
        />
      </div>

      <div className="form-group">
        <label className="form-label text-muted">Card Details</label>
        <div className="form-control card-element-wrapper" style={{ padding: "14px 12px" }}>
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: "16px",
                  color: "#2b3674",
                  fontFamily: "Inter, system-ui, sans-serif",
                  "::placeholder": {
                    color: "#a8a8a8",
                  },
                },
                invalid: {
                  color: "#ff4d4f",
                },
                complete: {
                  color: "#4a6cf7",
                },
              },
            }}
          />
        </div>
      </div>

      {error && <div className="stripe-error mb-3" style={{ color: "#ff4d4f", fontSize: "14px", marginTop: "5px" }}>{error}</div>}

      <div className="modal-actions">
        <button type="submit" className="btn btn-primary" disabled={!stripe || loading}>
          {loading ? "Processing..." : <><i className="bi bi-plus"></i> Add Card</>}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
      </div>
    </form>
  )
}

const cardValidationSchema = Yup.object().shape({
  cardNumber: Yup.string().matches(/^[0-9\s]{13,23}$/, "Invalid card number").required("Card number is required"),
  expiryDate: Yup.string().matches(/^(0[1-9]|1[0-2])\/\d{2}$/, "Format: MM/YY").required("Expiry date is required"),
  cvv: Yup.string().matches(/^[0-9]{3,4}$/, "CVV must be 3-4 digits").required("CVV is required"),
  nameOnCard: Yup.string().min(3, "Name must be at least 3 characters").required("Name on card is required"),
})


/* ========== Inline Stripe card form ========== */
const StripeCardForm = ({ clientSecret, intentType = "payment", amount, currency, onSuccess, onError, onCancel, plansDetails, showCardForm = true, selectedCard, useNewCardInModal, cards }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Case 1: Using saved card - skip CardElement validation
    if (!showCardForm && selectedCard) {
      setSubmitting(true)
      try {
        // Just call onSuccess with the saved paymentMethodId
        await onSuccess({ id: clientSecret, payment_method: selectedCard, type: "saved" })
      } catch (err) {
        console.error("Payment error", err)
        setError(err.message || "Payment failed")
        if (typeof onError === "function") onError(err)
      } finally {
        setSubmitting(false)
      }
      return
    }

    // Case 2: Using new card - validate CardElement
    if (!stripe || !elements) {
      setError("Stripe not loaded")
      return
    }
    const card = elements.getElement(CardElement)
    if (!card) {
      setError("Card element not found")
      return
    }

    setSubmitting(true)
    try {
      // First, create the payment method to check for duplicates
      const { paymentMethod, error: createError } = await stripe.createPaymentMethod({
        type: "card",
        card: card,
      })

      if (createError) {
        setError(createError.message)
        setSubmitting(false)
        return
      }

      if (paymentMethod && paymentMethod.card) {
        const existingCard = cards.find(card =>
          card.last4 === paymentMethod.card.last4 &&
          card.brand === paymentMethod.card.brand
        )
        if (existingCard) {
          // Use existing card for payment
          await onSuccess({ id: clientSecret, payment_method: existingCard.id, type: "saved" })
          setSubmitting(false)
          return
        }
      }

      // No duplicate, proceed with confirm using the created paymentMethod
      if (intentType === "setup") {
        const res = await stripe.confirmCardSetup(clientSecret, { payment_method: paymentMethod.id })
        if (res.error) throw res.error
        await onSuccess({ id: res.setupIntent.id, payment_method: res.setupIntent.payment_method, type: "setup" })
      } else {
        const res = await stripe.confirmCardPayment(clientSecret, { payment_method: paymentMethod.id })
        if (res.error) throw res.error
        await onSuccess({ id: res.paymentIntent.id, payment_method: res.paymentIntent.payment_method, type: "payment" })
      }
    } catch (err) {
      console.error("Stripe error", err)
      setError(err.message || "Payment failed")
      if (typeof onError === "function") onError(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="stripe-card-form">
      
      {/* Show card form only if showCardForm is true */}
      {showCardForm ? (
        <>
          <div className="form-group mb-3 border-black/15 border-b py-3 focus:border-blue-600 tabindex-0 bg-white">
            <div className="card-element-wrapper">
              <CardElement
                options={{
                  hidePostalCode: true,
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#1d1d1d",
                      fontFamily: "Inter, system-ui, sans-serif",
                      "::placeholder": {
                        color: "#a8a8a8",
                      },
                    },
                    invalid: {
                      color: "#ff4d4f",
                    },
                    complete: {
                      color: "#4a6cf7",
                    },
                  },
                }}
              />
            </div>
          </div>

          {error && <div className="stripe-error">{error}</div>}

          <div className="stripe-actions">
            <button type="button" className="btn btn-secondary" onClick={() => onCancel()} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={!stripe || submitting}>
              {submitting ? "Processing..." : intentType === "setup" ? "Add Payment Method" : `Pay ${currency} ${amount}`}
            </button>
          </div>
        </>
      ) : (
        /* If using saved card, show simplified form */
        <>
          {error && <div className="stripe-error">{error}</div>}

          <div className="stripe-actions">
            <button type="button" className="btn btn-secondary" onClick={() => onCancel()} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Processing..." : `Pay ${currency} ${amount}`}
            </button>
          </div>
        </>
      )}
    </form>
  )
}

/* ========== Subscription modal that consumes server-created clientSecret ========== */
const SubscriptionModal = ({ selectedPlan, paymentIntentPayload, onClose, onCreated, plansDetails, isFreeSubscription, mySubscription, selectedCard, useNewCardInModal, setUseNewCardInModal, cards }) => {
  const [step, setStep] = useState("ready") // ready | creating | success | error
  const [error, setError] = useState(null)
  const accessToken = typeof window !== "undefined" ? localStorage.getItem("access_token") : null

 // SubscriptionModal component ke andar (replace your existing handlePaymentSuccess)
const handlePaymentSuccess = async (result) => {
  setStep("creating")
  try {
    // Use selectedCard if user chose to use saved card, otherwise use the new card's payment_method
    const paymentMethodId = !useNewCardInModal && selectedCard ? selectedCard : result.payment_method

    const payload = {
      planId: selectedPlan.id || selectedPlan._id,
      paymentMethodId: paymentMethodId,
      paymentIntentId: result.id,
    }

    let res
    // Check if user already has an active subscription (not free)
    if (!isFreeSubscription && mySubscription?.id) {
      // Upgrade/change plan - use change-plan API
      res = await api.post(`/subscriptions/${mySubscription.id}/change-plan`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
    } else {
      // New subscription - create one
      res = await api.post("/subscriptions", payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
    }

    setStep("success")

    // pass server response back to parent so it can update UI
    if (typeof onCreated === "function") {
      onCreated(res.data)
    }

    // DO NOT immediately call onClose() here — let parent close the modal (so parent can update state first)
  } catch (err) {
    console.error("create subscription error", err)
    setError(err.response?.data?.message || err.message || "Failed to create subscription")
    setStep("error")
  }
}


  const handlePaymentError = (err) => {
    setError(err.message || "Payment failed")
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content stripe-payment-modal">
        <div className="modal-header mb-0">
          <h3 className="fw-semibold">{paymentIntentPayload.intentType === "setup" ? "Add Payment Method" : "Complete Payment"}</h3>
          <button className="btn-close" onClick={() => onClose()}></button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}

          {/* Checkout Plan Summary */}
          <div className="checkout-plan-summary">
            <div className="summary-header">Subscription Details</div>
            <div className="summary-details">
              <div>
                <span className="summary-plan-name">Name: {selectedPlan?.name || plansDetails?.name || 'Tier 1'}</span>
                <span className="text-muted d-block small mt-1">Plan: {selectedPlan?.interval || plansDetails?.interval || 'month'}</span>
              </div>
              <span className="summary-plan-price">
                ${selectedPlan?.price || plansDetails?.price || 0} / {selectedPlan?.interval || plansDetails?.interval || 'month'}
              </span>
            </div>
          </div>

          {/* Card selection options: Saved Card vs New Card - always visible when card is selected */}
          {selectedCard && (
            <div className="saved-card-option my-4">
              {(() => {
                const selectedCardData = cards.find(c => c.id === selectedCard)
                return (
                  <>
                    <div className="form-check mb-3">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="useSavedCard"
                        checked={!useNewCardInModal}
                        onChange={() => setUseNewCardInModal(false)}
                      />
                      <label className="form-check-label" htmlFor="useSavedCard">
                        Use Saved Card (ending in ****{selectedCardData?.last4 || ''})
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="useNewCard"
                        checked={useNewCardInModal}
                        onChange={() => setUseNewCardInModal(true)}
                      />
                      <label className="form-check-label" htmlFor="useNewCard">
                        Use a new card
                      </label>
                    </div>
                  </>
                )
              })()}
            </div>
          )}

          {step === "ready" && paymentIntentPayload.clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret: paymentIntentPayload.clientSecret }}>
              <StripeCardForm
                clientSecret={paymentIntentPayload.clientSecret}
                intentType={paymentIntentPayload.intentType}
                amount={selectedPlan.price || 0}
                currency={selectedPlan.currency || "USD"}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                plansDetails={plansDetails}
                onCancel={() => onClose()}
                showCardForm={useNewCardInModal || !selectedCard}
                selectedCard={selectedCard}
                useNewCardInModal={useNewCardInModal}
                cards={cards}
              />
            </Elements>
          )}

          {step === "creating" && (
            <div className="creating-sub">
              <h4>Creating subscription...</h4>
            </div>
          )}

          {step === "success" && (
            <div className="success-sub">
              <h4>Subscription activated</h4>
              <p>Redirecting...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ========== Main component ========== */
const SubscriptionManagement = ({ plans: incomingPlans = [], loading: propsLoading = false, refreshPlans }) => {
  const [activeTab, setActiveTab] = useState("plan")
  const [billingData, setBillingData] = useState([])
  const [billingLoading, setBillingLoading] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [mySubscription, setMySubscription] = useState(null)
  const [mySubLoading, setMySubLoading] = useState(false)
  const mySubFetchedRef = useRef(false)
  const [showAddCardModal, setShowAddCardModal] = useState(false)
  const [cards, setCards] = useState([])
  const [selectedCard, setSelectedCard] = useState(null)
  const [showCardDetailsModal, setShowCardDetailsModal] = useState(false)
  const [cardDetails, setCardDetails] = useState(null)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [cardToDelete, setCardToDelete] = useState(null)
  const [useNewCardInModal, setUseNewCardInModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [currentPlan, setCurrentPlan] = useState("free")
  const [autoRenewal, setAutoRenewal] = useState(true)
  const [plansDetails, setPlansDetails] = useState(null)
const [changeFlow, setChangeFlow] = useState({
  loading: false,
  validating: false,
  previewing: false,
  preview: null,
  error: null,
  readyToConfirm: false,
})
const [downgradeModal, setDowngradeModal] = useState({
  show: false,
  currentAssetCount: 0,
  newPlanLimit: 0,
  assetsToArchive: 0,
})
const navigate = useNavigate()
// helper: if user has a stripe-backed subscription (your API returns stripeSubscriptionId),
// we treat it as a normal upgrade (do not show change-plan flow)
// helper flag: agar backend ne Stripe subscription id di hai to treat as stripe-backed subscription
const isFreeSubscription = Boolean(
  !mySubscription?.id ||
  mySubscription?.subscriptionType === "free" ||
  mySubscription?.planId?.planType === "free" ||
  (mySubscription?.planId?.name && String(mySubscription.planId.name).toLowerCase().includes("free"))
)

// Check if the subscription is dead/inactive
const isCancelled = Boolean(
  mySubscription?.status === 'cancelled' ||
  mySubscription?.status === 'admin_cancelled' ||
  mySubscription?.status === 'expired' ||
  mySubscription?.status === 'inactive' ||
  mySubscription?.cancelledBy === 'admin'
)

const treatAsNew = isFreeSubscription || isCancelled

// Check if selected plan is an upgrade (higher price) than current plan
const getCurrentPlanPrice = () => {
  if (!mySubscription) return 0
  const planRef = mySubscription.planId || mySubscription.plan || null
  if (!planRef) return 0
  return typeof planRef.price !== "undefined" ? planRef.price : planRef.monthlyPrice ?? planRef.yearlyPrice ?? 0
}

const isUpgrade = selectedPlan && !treatAsNew && selectedPlan.price > getCurrentPlanPrice()
const isSelectedSameAsCurrent = selectedPlan && String(selectedPlan.id) === String(currentPlan)

const getSubscriptionId = () => {
  if (!mySubscription) return null
  return (
    mySubscription.id ||
    mySubscription._id ||
    (mySubscription.subscription && (mySubscription.subscription.id || mySubscription.subscription._id)) ||
    null
  )
}
 const handleStartChangePlan = async () => {
  const subscriptionId = getSubscriptionId()
  if (!subscriptionId) {
    showErrorToast("No active subscription found to change")
    return
  }
  if (!selectedPlan || !selectedPlan.id) {
    showErrorToast("Select a plan to change to")
    return
  }

  const currentPlanPrice = getCurrentPlanPrice()
  const isDowngrade = selectedPlan.price < currentPlanPrice

  // For downgrade: fetch digital asset count, show warning modal
  if (isDowngrade) {
    setChangeFlow((s) => ({ ...s, validating: true, error: null }))
    try {
      const digitalRes = await api.get(
        '/assets?assetCategory=digital&page=1&limit=1&sortBy=updatedAt:desc',
        { headers: getAuthHeaders() }
      )
      const totalDigitalAssets = digitalRes.data?.totalResults ?? digitalRes.data?.results?.length ?? 0
      const newDigitalLimit = selectedPlan.limits?.digitalAssets?.total ?? selectedPlan.limits?.digitalAssets ?? 0
      const limitValue = newDigitalLimit === -1 ? Infinity : newDigitalLimit
      const assetsToArchive = Math.max(0, totalDigitalAssets - limitValue)
      setDowngradeModal({
        show: true,
        currentAssetCount: totalDigitalAssets,
        newPlanLimit: newDigitalLimit === -1 ? 'Unlimited' : newDigitalLimit,
        assetsToArchive,
      })
    } catch (err) {
      showErrorToast("Failed to fetch asset info")
    } finally {
      setChangeFlow((s) => ({ ...s, validating: false }))
    }
    return
  }

  // For upgrade/same-tier: existing validate + preview flow
  setChangeFlow((s) => ({ ...s, validating: true, error: null, preview: null, readyToConfirm: false }))
  try {
    await api.post(
      `/subscriptions/${subscriptionId}/validate-change`,
      { planId: selectedPlan.id },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    )
    const previewRes = await api.post(
      `/subscriptions/${subscriptionId}/preview-change`,
      { planId: selectedPlan.id },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    )
    const previewData = previewRes?.data || {}
    setChangeFlow((s) => ({ ...s, validating: false, preview: previewData, readyToConfirm: true }))
    showSuccessToast("Preview ready. Review and confirm change.")
  } catch (err) {
    console.error("change plan validate/preview error", err)
    const msg = err.response?.data?.message || err.message || "Failed to validate/preview change"
    setChangeFlow((s) => ({ ...s, validating: false, error: msg, preview: null }))
    showErrorToast(msg)
  }
}

// confirm change -> call change-plan
const handleConfirmChangePlan = async () => {
  const subscriptionId = getSubscriptionId()
  if (!subscriptionId) {
    showErrorToast("No active subscription found to change")
    return
  }
  const currentPlanPrice = getCurrentPlanPrice()
  const isDowngrade = selectedPlan && selectedPlan.price < currentPlanPrice

  setChangeFlow((s) => ({ ...s, loading: true }))
  setDowngradeModal((s) => ({ ...s, show: false }))
  try {
    const res = await api.post(
      `/subscriptions/${subscriptionId}/change-plan`,
      { planId: selectedPlan.id },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    )

    showSuccessToast(isDowngrade ? "Plan downgraded. All digital assets have been archived. Reactivate the ones you need." : "Plan changed successfully!")
    setChangeFlow({ loading: false, validating: false, previewing: false, preview: null, error: null, readyToConfirm: false })
    
    try { await fetchMySubscription() } catch (e) { console.warn(e) }
    const sub = res.data?.subscription || res.data || {};
    const newPlanType = sub.planId?.planType || selectedPlan?.planType || '';
    localStorage.setItem('planType', newPlanType);
    setTimeout(() => {
      window.dispatchEvent(new Event('planTypeChanged'));
    }, 100);
    if (typeof refreshPlans === "function") {
      try { await refreshPlans() } catch (e) { console.warn(e) }
    }

    const newPlanId = res?.data?.planId || res?.data?.subscription?.planId || selectedPlan?.id
    if (newPlanId) {
      setCurrentPlan(String(newPlanId))
      setSelectedPlan(selectedPlan)
    }

    if (isDowngrade) {
      navigate('/my-assets?tab=archived')
    } else {
      const newDigitalLimit = selectedPlan?.limits?.digitalAssets?.total ?? selectedPlan?.limits?.digitalAssets ?? -1
      const reactivatedCount = await reactivateAllArchivedAssets(newDigitalLimit)
      if (reactivatedCount > 0) {
        showSuccessToast('Archived assets have been reactivated!')
        navigate('/my-assets?tab=reactivated')
      }
    }
  } catch (err) {
    console.error("change-plan error", err)
    const msg = err.response?.data?.message || err.message || "Failed to change plan"
    showErrorToast(msg)
    setChangeFlow((s) => ({ ...s, loading: false, error: msg }))
  }
}

const handleCancelChangeFlow = () => {
  setChangeFlow({ loading: false, validating: false, previewing: false, preview: null, error: null, readyToConfirm: false })
}

// Bulk reactivate archived assets after upgrade — respects new plan's digital asset limit
const reactivateAllArchivedAssets = async (newPlanDigitalLimit = -1) => {
  try {
    const archivedRes = await api.get('/assets/archived', { headers: getAuthHeaders() })
    const archived = archivedRes.data?.results || archivedRes.data || []
    if (!Array.isArray(archived) || archived.length === 0) return 0

    // Sort newest first, then apply limit
    const sorted = [...archived].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
    const toReactivate = newPlanDigitalLimit === -1 ? sorted : sorted.slice(0, newPlanDigitalLimit)
    const assetIds = toReactivate.map((a) => a.id || a._id).filter(Boolean)
    if (assetIds.length === 0) return 0

    await api.post('/assets/bulk-reactivate', { assetIds }, { headers: getAuthHeaders() })
    return assetIds.length
  } catch (e) {
    console.warn('reactivateAllArchivedAssets failed:', e)
    return 0
  }
}

  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentIntentPayload, setPaymentIntentPayload] = useState({ clientSecret: "", paymentIntentId: "", setupIntentId: "", intentType: "" })
  const [paymentLoadingInit, setPaymentLoadingInit] = useState(false)

  const accessToken = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
  const apiPlans = Array.isArray(incomingPlans) ? incomingPlans : []

  const mySubPlan = mySubscription?.planId && typeof mySubscription.planId === "object" ? mySubscription.planId : null
  const mySubPlanId = mySubPlan ? (mySubPlan.id || mySubPlan._id || String(mySubPlan._id)) : null
  const isMySubPlanInApi = mySubPlanId ? apiPlans.some((p) => String(p.id ?? p._id) === String(mySubPlanId)) : false

  const displayPlans = useMemo(() => {
    if (mySubPlan && !isMySubPlanInApi) {
      return [mySubPlan, ...apiPlans]
    }
    return apiPlans
  }, [apiPlans, mySubPlan, isMySubPlanInApi])

  // normalize plan
  const normalizePlan = (p) => {
    if (!p) return null
    const id = p.id || p._id || (p._id && String(p._id)) || null
    const price = typeof p.price !== "undefined" ? p.price : p.monthlyPrice ?? p.yearlyPrice ?? null
    const interval = p.interval || (p.monthlyPrice ? "month" : p.period) || "month"
    const limitsFromPlan = p.limits || {}
    const toLimit = (v) => {
      if (typeof v === "number") return { used: 0, total: v }
      if (v && typeof v === "object") return { used: v.used ?? v.current ?? 0, total: v.limit ?? -1 }
      return { used: 0, total: -1 }
    }
    const limits = {
      digitalAssets: toLimit(limitsFromPlan.digitalAssets),
      physicalAssets: typeof limitsFromPlan.physicalAssets !== "undefined" ? limitsFromPlan.physicalAssets : -1,
      collaborators: toLimit(limitsFromPlan.collaborators),
      aiExtraction: toLimit(limitsFromPlan.aiExtractions),
    }
    return { ...p, id, price, interval, limits }
  }

  const safePercent = (used, total) => {
    if (typeof total !== "number" || total <= 0) return 100
    if (!used) return 0
    return Math.min(100, Math.round((used / total) * 100))
  }

  // features helpers
  const FEATURE_LABELS = {
  dashboardTrial: (v) =>
    v && v.enabled && v.durationDays
      ? `Dashboard Trial (${v.durationDays} Days)`
      : null,  // ❗ enabled false hoga to null → skip

  dashboard: (v) =>
    v === "advanced" ? "Advanced Dashboard" : "Basic Dashboard",

  legacyAccess: "Legacy Access",
  exportPdfCsv: "Export PDF/CSV",
  customTagsFilters: "Custom Tags & Filters",
  autoReminders: "Auto Reminders",
  assetHistory: "Asset History",
  assetValuation: "Asset Valuation",
  emailPhoneSupport: "Email/Phone Support",
}

// ---------- isFeatureEnabled ----------
const isFeatureEnabled = (featuresObj, key) => {
  if (!featuresObj) return false

  // dashboardTrial: only enabled when explicit enabled === true
  if (key === "dashboardTrial") {
    const val = featuresObj[key]
    return !!(val && val.enabled)
  }

  // dashboard: treat as always present (enabled) if string exists — UI will show Basic/Advanced.
  if (key === "dashboard") {
    return typeof featuresObj[key] !== "undefined" && featuresObj[key] !== null
  }

  const val = featuresObj[key]
  if (typeof val === "boolean") return val
  if (!val) return false
  if (typeof val === "object") {
    return typeof val.enabled !== "undefined" ? !!val.enabled : true
  }
  return !!val
}

// ---------- renderFeaturesGrid ----------
const renderFeaturesGrid = (featuresObj) => {
  if (!featuresObj) return null
  const keys = Object.keys(FEATURE_LABELS)

  return (
    <div className="features-section">
      <h4>Features:</h4>
      <div className="features-grid">
        {keys.map((key) => {
          const rawValue = featuresObj[key]
          const labelGetter = FEATURE_LABELS[key]
          const label = typeof labelGetter === "function" ? labelGetter(rawValue) : labelGetter

          // Skip features that intentionally return null (like disabled dashboardTrial)
          if (!label) return null

          const enabled = isFeatureEnabled(featuresObj, key)

          return (
            <div key={key} className="feature-item">
              {enabled ? <CircleCheck className="check-icon" /> : <Circle className="uncheck-icon" />}
              <span>{label}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}


  // fetch my subscription
  const fetchMySubscription = async () => {
    setMySubLoading(true)
    try {
      const res = await api.get("/subscriptions/my", { headers: { Authorization: `Bearer ${accessToken}` } })
      const data = res?.data || {}
      const sub = data.subscription || data.plan || data
      setMySubscription(sub)
      if (sub?.planId || sub?.id) setCurrentPlan(sub.planId?.id || sub.planId || sub.id)
    } catch (err) {
      console.error("Failed to fetch my subscription", err)
    } finally {
      setMySubLoading(false)
    }
  }

  useEffect(() => {
    if (typeof refreshPlans === "function") {
      try {
        refreshPlans()
      } catch (e) {}
    }
    if (!mySubFetchedRef.current) {
      mySubFetchedRef.current = true
      fetchMySubscription()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    try {
      if (!mySubscription) return
      const planRef = mySubscription.planId || mySubscription.plan || null
      if (!planRef) return
      const planRefId = typeof planRef === "string" ? planRef : planRef.id || planRef._id || null
      const foundRaw = displayPlans.find((p) => String(p.id ?? p._id) === String(planRefId))
      const baseRawPlan = foundRaw || (typeof planRef === "object" ? planRef : null)
      const normalized = normalizePlan(baseRawPlan)
      if (normalized) {
        const usage = mySubscription.usageTracking || mySubscription.usage || {}
        const mergedLimits = {
          digitalAssets: {
            used: usage.digitalAssets?.current ?? usage.digitalAssets?.used ?? normalized.limits.digitalAssets.used ?? 0,
            total: normalized.limits.digitalAssets.total ?? (usage.digitalAssets?.limit ?? -1),
          },
          physicalAssets: normalized.limits.physicalAssets ?? (usage.physicalAssets?.limit ?? -1),
          collaborators: {
            used: usage.collaborators?.current ?? usage.collaborators?.used ?? normalized.limits.collaborators.used ?? 0,
            total: normalized.limits.collaborators.total ?? (usage.collaborators?.limit ?? -1),
          },
          aiExtraction: {
            used: usage.aiExtractions?.used ?? usage.aiExtractions?.current ?? normalized.limits.aiExtraction.used ?? 0,
            total: normalized.limits.aiExtraction.total ?? (usage.aiExtractions?.limit ?? -1),
          },
        }
        setSelectedPlan({ ...normalized, limits: mergedLimits })
        if (normalized.id) setCurrentPlan(String(normalized.id))
      }
    } catch (e) {
      console.error("merge plan error", e)
    }
  }, [mySubscription, incomingPlans])

  // billing
  const fetchBillingHistory = async (page = 1, limit = 10) => {
    setBillingLoading(true)
    try {
      const res = await api.get(`/subscriptions/billing-history?page=${page}&limit=${limit}`, { headers: { Authorization: `Bearer ${accessToken}` } })
      const data = res?.data?.billingHistory || res?.data?.results || res?.data?.invoices || res?.data || []
      const items = Array.isArray(data) ? data : data.items || data.records || []
      setBillingData(items)
    } catch (err) {
      console.error("Failed to fetch billing history", err)
      setBillingData([])
    } finally {
      setBillingLoading(false)
    }
  }

  // cards
  const fetchCards = async () => {
    try {
      setLoading(true)
      const res = await api.get("/subscriptions/payment-methods", { headers: { Authorization: `Bearer ${accessToken}` } })
      const data = res?.data || {}
      const methods = data.cards || data.paymentMethods || data.methods || data.payment_methods || []
      if (Array.isArray(methods) && methods.length > 0) {
        const normalized = methods.map((m) => ({
          id: m.id ?? m._id ?? m.cardId ?? m.paymentMethodId ?? m.token ?? null,
          last4: m.last4 ?? m.cardLast4 ?? (m.cardNumber ? String(m.cardNumber).slice(-4) : undefined),
          brand: m.brand ?? m.cardBrand ?? m.network ?? "",
          name: m.name ?? m.cardHolderName ?? m.billingName ?? "",
          default: !!m.default || !!m.isDefault || !!m.is_default,
          isDefault: !!m.default || !!m.isDefault || !!m.is_default,
          expiryMonth: m.expiryMonth ?? m.expMonth ?? m.exp_month ?? null,
          expiryYear: m.expiryYear ?? m.expYear ?? m.exp_year ?? null,
          _raw: m,
        }))
        setCards(normalized)
        const defaultCard = normalized.find((c) => c.default) || normalized[0]
        if (defaultCard) setSelectedCard(defaultCard.id)
      } else {
        setCards([])
      }
    } catch (err) {
      console.error("Failed to fetch cards", err)
      setSelectedCard(null)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectPlan = (planOrId) => {
    try {
      if (!planOrId) return
      const raw = typeof planOrId === "string" || typeof planOrId === "number" ? displayPlans.find((p) => String(p.id ?? p._id) === String(planOrId)) : planOrId
      const normalized = normalizePlan(raw || planOrId)
      if (!normalized) return
      setSelectedPlan(normalized)
       setPlansDetails(normalized)
    } catch (err) {
      console.error("handleSelectPlan error", err)
    }
  }

  const handleAddCardSubmit = async (paymentMethod) => {
    try {
      // Check for duplicate card if card details are available
      if (paymentMethod.card) {
        const existingCard = cards.find(card =>
          card.last4 === paymentMethod.card.last4 &&
          card.brand === paymentMethod.card.brand
        )
        if (existingCard) {
          showErrorToast("This card is already added. Please use a different card.")
          return
        }
      }

      setPaymentLoading(true)
      const response = await api.post(
        "/subscriptions/payment-methods",
        { paymentMethodId: paymentMethod.id },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      )
      // Refresh cards list to get the newly added card with proper structure
      await fetchCards()
      setShowAddCardModal(false)
      showSuccessToast("Card added successfully!")
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to add card. Please try again.")
      console.error("Add card error:", err)
    } finally {
      setPaymentLoading(false)
    }
  }

  const handleUpgradeClick = async () => {
    if (!selectedPlan) {
      const fallbackRaw = displayPlans.find((p) => String(p.id ?? p._id) === String(currentPlan))
      if (fallbackRaw) setSelectedPlan(normalizePlan(fallbackRaw))
    }
    await fetchCards()
    setActiveTab("payment")
    try {
      const el = document.querySelector(".payment-content")
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth" })
    } catch (e) {}
  }

  // creates server-side PaymentIntent/SetupIntent and opens modal
  const handleOpenPayment = async () => {
    if (!selectedPlan || !selectedPlan.id) {
      showErrorToast("Please select a plan first")
      return
    }
    try {
      setPaymentLoadingInit(true)
      // Pass selected card ID if user has selected a saved card
      const paymentData = { 
        planId: selectedPlan.id,
        ...(selectedCard && { paymentMethodId: selectedCard })
      }
      const res = await api.post("/subscriptions/payment-intent", paymentData, { headers: { Authorization: `Bearer ${accessToken}` } })
      const data = res?.data || {}
      const clientSecret = data.clientSecret || data.paymentIntentClientSecret || data.setupClientSecret
      const paymentIntentId = data.paymentIntentId || data.payment_intent_id || ""
      const setupIntentId = data.setupIntentId || data.setup_intent_id || ""
      const intentType = data.requiresPayment ? "payment" : data.requiresPaymentMethod ? "setup" : data.intentType || "payment"
      if (!clientSecret) throw new Error("No client secret returned from server")
      setPaymentIntentPayload({ clientSecret, paymentIntentId, setupIntentId, intentType })
      setShowPaymentModal(true)
      setPlansDetails(data.plan)
    } catch (err) {
      console.error("Failed to init payment intent", err)
      showErrorToast(err.response?.data?.message || "Failed to start payment. Try again.")
    } finally {
      setPaymentLoadingInit(false)
    }
  }

  // If user wants to charge existing card path: call backend to charge saved card
  const handleProcessPayment = async () => {
    if (!selectedPlan) {
      showErrorToast("Please select a plan")
      return
    }
    // prefer collecting a fresh intent (recommended). If you want to support charging saved card directly, implement here.
    await handleOpenPayment()
  }

  const handleDeleteCard = async (cardId) => {
    try {
      setLoading(true)
      await api.delete(`/subscriptions/payment-methods/${cardId}`, { headers: { Authorization: `Bearer ${accessToken}` } })
      // Refresh cards list from server to ensure state consistency
      await fetchCards()
      showSuccessToast("Card deleted successfully!")
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to delete card")
      console.error("Delete card error:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleViewCardDetails = (card) => {
    setCardDetails(card)
    setShowCardDetailsModal(true)
  }

  const handleSetDefaultCard = async (cardId) => {
    try {
      setLoading(true)
      await api.post(`/subscriptions/payment-methods/set-default`, { paymentMethodId: cardId }, { headers: { Authorization: `Bearer ${accessToken}` } })
      // Refresh cards list from server to ensure state consistency
      await fetchCards()
      showSuccessToast("Default card updated!")
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update default card")
      console.error("Set default card error:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadInvoice = (invoiceId) => {
    try {
      const link = document.createElement("a")
      link.href = `/api/invoices/${invoiceId}/pdf`
      link.download = `invoice-${invoiceId}.pdf`
      link.click()
      showSuccessToast("Invoice downloading...")
    } catch (err) {
      showErrorToast("Failed to download invoice")
    }
  }

  const renderSelectedPlanRight = (plan) => {
    if (!plan) return null

    const isB2bActive =
      mySubscription?.subscriptionType === "b2b" ||
      plan?.planType === "b2b" ||
      mySubscription?.planId?.planType === "b2b"

    const contractEnd =
      mySubscription?.b2bDetails?.contractEndDate ||
      mySubscription?.currentPeriodEnd ||
      plan?.currentPeriodEnd ||
      null

    const formatDate = (dateVal) => {
      if (!dateVal) return null
      try {
        const d = new Date(dateVal)
        if (isNaN(d.getTime())) return null
        return d.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      } catch (e) {
        return null
      }
    }

    const validTillStr = formatDate(contractEnd)

    return (
      <div className="plans-right">
        <div className="usage-section">
          <div className="usage-header d-flex justify-content-between align-items-center mb-3">
            <h4 className="m-0">Usage Limit:</h4>
            {isB2bActive && (
              <span className="b2b-valid-till">
                {validTillStr ? `Valid till: ${validTillStr}` : "Valid till: Ongoing"}
              </span>
            )}
          </div>
          <div className="usage-grid">
            <div className="usage-item">
              <div className="d-flex align-items-center gap-2 justify-between">
                <span className="usage-label">Digital Assets</span>
                <span className="usage-value">
                  {plan.limits?.digitalAssets?.total === -1 ? `${plan.limits?.digitalAssets?.used}/∞` : `${plan.limits?.digitalAssets?.used}/${plan.limits?.digitalAssets?.total}`}
                </span>
              </div>
              <div className="usage-bar">
                <div className="usage-fill" style={{ width: `${safePercent(plan.limits?.digitalAssets?.used, plan.limits?.digitalAssets?.total)}%` }}></div>
              </div>
            </div>

            <div className="usage-item">
              <span className="usage-label">Physical Assets</span>
              <span className="usage-value">{plan.limits?.physicalAssets === -1 ? "∞" : plan.limits?.physicalAssets}</span>
            </div>

            <div className="usage-item">
              <div className="d-flex align-items-center gap-2 justify-between">
                <span className="usage-label">Collaborators</span>
                <span className="usage-value">
                  {plan.limits?.collaborators?.total === -1 ? `${plan.limits?.collaborators?.used}/∞` : `${plan.limits?.collaborators?.used}/${plan.limits?.collaborators?.total}`}
                </span>
              </div>
              <div className="usage-bar">
                <div className="usage-fill" style={{ width: `${safePercent(plan.limits?.collaborators?.used, plan.limits?.collaborators?.total)}%` }}></div>
              </div>
            </div>

            <div className="usage-item">
              <div className="d-flex align-items-center gap-2 justify-between">
                <span className="usage-label">AI Extraction</span>
                <span className="usage-value">
                  {plan.limits?.aiExtraction?.total === -1 ? `${plan.limits?.aiExtraction?.used}/∞` : `${plan.limits?.aiExtraction?.used}/${plan.limits?.aiExtraction?.total}`}
                </span>
              </div>
              <div className="usage-bar">
                <div className="usage-fill" style={{ width: `${safePercent(plan.limits?.aiExtraction?.used, plan.limits?.aiExtraction?.total)}%` }}></div>
              </div>
            </div>
          </div>
        </div>

        {renderFeaturesGrid(plan.features)}

        <div className="auto-renewal">
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" id="autoRenewal" checked={autoRenewal} onChange={() => setAutoRenewal(!autoRenewal)} />
            <label className="form-check-label" htmlFor="autoRenewal">Enable auto-renewal</label>
          </div>
        </div>
      </div>
    )
  }

  const renderCurrentPlan = () => {
    const rawCurrent = displayPlans.find((p) => String(p.id ?? p._id) === String(currentPlan))
    const planForCurrent = normalizePlan(rawCurrent)
    const planToShow = selectedPlan && String(selectedPlan.id) === String(planForCurrent?.id) ? selectedPlan : planForCurrent
    return renderSelectedPlanRight(planToShow)
  }

  const handleTabClick = (tab) => {
    setActiveTab(tab)
    if (tab === "billing") {
      fetchBillingHistory()
      fetchCards()
    }
    if (tab === "payment") fetchCards()
  }

  return (
    <div className="subscription-container">
      {(loading || paymentLoading) && <Loader />}

      <div className="subscription-tabs">
        <button className={`tab-btn ${activeTab === "plan" ? "active" : ""}`} onClick={() => handleTabClick("plan")}>Plan</button>
        <button className={`tab-btn ${activeTab === "payment" ? "active" : ""}`} onClick={() => handleTabClick("payment")}>Payment</button>
        <button className={`tab-btn ${activeTab === "billing" ? "active" : ""}`} onClick={() => handleTabClick("billing")}>Billing</button>
      </div>

      {/* Plan Tab */}
      {activeTab === "plan" && (
        <div className="tab-content plan-content">
          <div className="plans-grid">
            <div className="plans-left">
              {displayPlans.map((planRaw) => {
                const plan = normalizePlan(planRaw)
                const isSelected = selectedPlan && selectedPlan.id ? String(selectedPlan.id) === String(plan?.id ?? planRaw?.id ?? planRaw?._id) : false
                const isCurrent = currentPlan ? String(currentPlan) === String(plan?.id ?? planRaw?.id ?? planRaw?._id) : false
                const isB2b = planRaw?.planType === "b2b" || planRaw?.subscriptionType === "b2b" || (mySubscription?.subscriptionType === "b2b" && isCurrent)
                return (
                  <div key={plan?.id || planRaw?._id || Math.random()} className={`plan-card ${isSelected ? "active" : ""} ${isCurrent ? "current" : ""}`} onClick={() => handleSelectPlan(planRaw)}>
                    <div className="flex justify-between items-center">
                      <div className="plan-header">
                        <div className="plan-checkbox"><input type="checkbox" checked={isSelected} readOnly /></div>
                        <div className="plan-info">
                          <h3 className="plan-name">{planRaw.name}</h3>
                          {isCurrent ? (
                            <span className="plan-badge active-badge">Active</span>
                          ) : isB2b ? (
                            <span className="plan-badge save-badge">B2B Plan</span>
                          ) : planRaw.yearlyPrice < 150 ? (
                            <span className="plan-badge save-badge">Save 20%</span>
                          ) : (
                            <span className="plan-badge save-badge">Save 30%</span>
                          )}
                          {planRaw.since && <span className="plan-since">Since {planRaw.since}</span>}
                        </div>
                      </div>

                      <div className="plan-price">
                        {isB2b && (plan?.price === 0 || mySubscription?.amount === 0 || planRaw?.amount === 0) ? (
                          <span className="price-contact">Assigned by admin</span>
                        ) : typeof plan?.price !== "undefined" && plan?.price !== null ? (
                          <>
                            <span className="price-value">${plan.price}</span>
                            <span className="price-period">/{plan.interval}</span>
                          </>
                        ) : (
                          <span className="price-contact">{planRaw.cta}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}

             {/* Upgrade or Change plan button logic */}
{!selectedPlan ? (
  <button className="btn btn-primary upgrade-plan-btn" disabled>
    Upgrade Plan
  </button>
) : (
  <>
    {treatAsNew ? (
      // user currently on FREE -> normal upgrade/payment flow (new subscription)
      <button
        className="btn btn-primary upgrade-plan-btn"
        onClick={handleUpgradeClick}
        disabled={paymentLoading || loading || isSelectedSameAsCurrent}
      >
        {isSelectedSameAsCurrent ? "Current plan" : "Upgrade Plan"}
      </button>
    ) : isUpgrade ? (
      // user currently on PAID and upgrading to higher tier -> need preview first, then payment
      <>
        {isSelectedSameAsCurrent ? (
          <button className="btn btn-secondary" disabled>
            Current Plan
          </button>
        ) : (
          <>
            <button
              className="btn btn-warning"
              onClick={handleStartChangePlan}
              disabled={changeFlow?.validating || changeFlow?.loading}
            >
              {changeFlow?.validating ? "Checking..." : "Upgrade Plan"}
            </button>

            {changeFlow?.readyToConfirm && changeFlow?.preview && (
              <div className="change-preview mt-3">
                <div className="preview-summary">
                  <p>Effective: {changeFlow.preview.effectiveDate ?? "immediate"}</p>
                </div>

                <div className="preview-actions d-flex gap-2 mt-2">
                  <button className="btn btn-danger" onClick={() => {
                    // Instead of calling backend, switch to Payment tab for upgrade
                    handleCancelChangeFlow()
                    fetchCards()
                    setActiveTab("payment")
                  }} disabled={changeFlow.loading}>
                    {changeFlow.loading ? "Loading..." : "Proceed to Payment"}
                  </button>
                  <button className="btn btn-secondary" onClick={handleCancelChangeFlow} disabled={changeFlow.loading}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {changeFlow?.error && <div className="text-danger mt-2">{changeFlow.error}</div>}
          </>
        )}
      </>
    ) : (
      // user currently on PAID and downgrading/same tier -> direct change-flow
      <>
        {isSelectedSameAsCurrent ? (
          <button className="btn btn-secondary" disabled>
            Current Plan
          </button>
        ) : (
          <>
            <button
              className="btn btn-warning"
              onClick={handleStartChangePlan}
              disabled={changeFlow?.validating || changeFlow?.loading}
            >
              {changeFlow?.validating ? "Checking..." : "Change plan"}
            </button>

            {changeFlow?.readyToConfirm && changeFlow?.preview && (
              <div className="change-preview mt-3">
                <div className="preview-summary">
                  <p>Effective: {changeFlow.preview.effectiveDate ?? "immediate"}</p>
                </div>

                <div className="preview-actions d-flex gap-2 mt-2">
                  <button className="btn btn-danger px-5" onClick={handleConfirmChangePlan} disabled={changeFlow.loading}>
                    {changeFlow.loading ? "Applying..." : "Confirm Change Plan"}
                  </button>
                  <button className="btn btn-secondary px-5" onClick={handleCancelChangeFlow} disabled={changeFlow.loading}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {changeFlow?.error && <div className="text-danger mt-2">{changeFlow.error}</div>}
          </>
        )}
      </>
    )}
  </>
)}


            </div>

            {/* Right pane */}
            {selectedPlan ? renderSelectedPlanRight(selectedPlan) : renderCurrentPlan()}
          </div>
        </div>
      )}

      {/* Payment Tab */}
      {activeTab === "payment" && (
        <div className="tab-content payment-content">
          {selectedPlan ? (
            <>
              <div className="payment-header">
                <h3>Payment Method</h3>
                <p>Upgrade to {selectedPlan.name} Plan - ${selectedPlan.price}/{selectedPlan.interval}</p>
              </div>

              <div className="payment-option stripe-option">
                <div className="option-header">
                  <input type="radio" name="payment" id="stripePay" defaultChecked />
                  <label htmlFor="stripePay">Stripe Pay</label>
                </div>
                <img src="/stripe.png" alt="Stripe" />
              </div>

              {/* Cards */}
              <div className="cards-section">
                <div className="cards-header">
                  <h4>Credit / Debit Cards</h4>
                {cards.length > 0 ? (
                  <button
                    className="btn btn-link add-card-link d-flex align-items-center gap-1 p-0 text-decoration-none"
                    onClick={() => setShowAddCardModal(true)}
                  >
                    <PlusCircleIcon width={16} height={16} /> Add new card
                  </button>
                  ) : null}
                </div>

                {cards.length > 0 ? (
                  <div className="cards-list">
                    {cards.map((card) => (
                      <div 
                        key={card.id} 
                        className={`card-item ${selectedCard === card.id ? 'selected' : ''}`}
                        onClick={() => setSelectedCard(card.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-radio">
                          <input 
                            type="radio" 
                            name="selectedCard" 
                            checked={selectedCard === card.id} 
                            onChange={() => setSelectedCard(card.id)}
                          />
                        </div>
                        <div className="card-logo">{card.brand === "mastercard" && <span className="mastercard">●●</span>}{card.brand === "visa" && <span className="visa">VI</span>}</div>
                        <div className="card-details"><p className="card-name">{card.name}</p><p className="card-number">XXXX XXXX XXXX {card.last4}</p></div>
                        <div className="card-badge"><span className="badge">{card.brand}</span>{card.default && <span className="default-badge">Default</span>}</div>
                        <div className="card-actions">
                          <button type="button" className="btn btn-sm btn-view" onClick={(e) => { e.stopPropagation(); handleViewCardDetails(card); }} title="View card details"><Eye size={16} /></button>
                          {!card.default && <button type="button" className="btn btn-sm" onClick={(e) => { e.stopPropagation(); handleSetDefaultCard(card.id); }}>Set default</button>}
                          <button type="button" className="btn btn-sm btn-delete" onClick={(e) => { e.stopPropagation(); setCardToDelete(card.id); setShowDeleteConfirmModal(true); }}><i className="bi bi-trash"></i></button>
                        </div>
                      </div>
                    ))}
                    {/* Option to add new card */}
                    <div 
                      className="card-item card-item-new"
                      onClick={() => setShowAddCardModal(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="card-details">
                        <p className="card-name d-flex align-items-center gap-1 m-0"> <PlusCircleIcon width={16} height={16} /> Add new card</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-cards">
                    <i className="bi bi-credit-card"></i>
                    <p>You don't have any cards</p>
                    <button className="btn btn-primary" onClick={() => setShowAddCardModal(true)}><i className="bi bi-plus"></i> Add Card</button>
                  </div>
                )}
              </div>

              <button className="btn btn-primary btn-proceed" onClick={handleProcessPayment} disabled={paymentLoadingInit || paymentLoading || (cards.length > 0 && !selectedCard)}>
                {paymentLoadingInit ? "Preparing..." : "Proceed"} <i className="bi bi-arrow-right"></i>
              </button>
            </>
          ) : (
            <div className="no-selection">
              <p>Please select a plan first to proceed with payment.</p>
              <button className="btn btn-primary" onClick={() => setActiveTab("plan")}>Select Plan</button>
            </div>
          )}
        </div>
      )}

      {/* Billing Tab */}
      {activeTab === "billing" && (
        <div className="tab-content billing-content">
          {/* Cards Section */}
          <div className="cards-section mb-4">
            <div className="cards-header">
              <h4>Credit / Debit Cards</h4>
              {cards.length > 0 ? (
                <button
                  className="btn btn-link add-card-link d-flex align-items-center gap-1 p-0 text-decoration-none"
                  onClick={() => setShowAddCardModal(true)}
                >
                  <PlusCircleIcon width={16} height={16} /> Add new card
                </button>
              ) : null}
            </div>

            {cards.length > 0 ? (
              <div className="cards-list">
                {cards.map((card) => (
                  <div 
                    key={card.id} 
                    className="card-item"
                    style={{ cursor: 'default' }}
                  >
                    <div className="card-logo">
                      {card.brand === "mastercard" && <span className="mastercard">●●</span>}
                      {card.brand === "visa" && <span className="visa">VI</span>}
                    </div>
                    <div className="card-details">
                      <p className="card-name">{card.name}</p>
                      <p className="card-number">XXXX XXXX XXXX {card.last4}</p>
                    </div>
                    <div className="card-badge">
                      <span className="badge">{card.brand}</span>
                      {card.default && <span className="default-badge">Default</span>}
                    </div>
                    <div className="card-actions">
                      <button 
                        type="button" 
                        className="btn btn-sm btn-view" 
                        onClick={(e) => { e.stopPropagation(); handleViewCardDetails(card); }} 
                        title="View card details"
                      >
                        <Eye size={16} />
                      </button>
                      {!card.default && (
                        <button 
                          type="button" 
                          className="btn btn-sm" 
                          onClick={(e) => { e.stopPropagation(); handleSetDefaultCard(card.id); }}
                        >
                          Set default
                        </button>
                      )}
                      <button 
                        type="button" 
                        className="btn btn-sm btn-delete" 
                        onClick={(e) => { e.stopPropagation(); setCardToDelete(card.id); setShowDeleteConfirmModal(true); }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-cards">
                <p>You don't have any cards</p>
                <button
                  className="btn btn-primary btn-add-card d-flex align-items-center gap-1"
                  onClick={() => setShowAddCardModal(true)}
                >
                  <PlusCircleIcon width={16} height={16} /> Add card
                </button>
              </div>
            )}
          </div>

          <div className="billing-header"><h3>Billing & Invoices</h3></div>

          {billingLoading ? <div className="no-invoices"><p>Loading billing history...</p></div> : billingData.length > 0 ? (
            <div className="invoices-table">
              <table>
                <thead>
                  <tr><th className="table-heading">Invoice ID</th><th className="table-heading">Date</th><th className="table-heading">Plan</th><th className="table-heading">Amount</th><th className="table-heading">Status</th><th className="table-heading">Actions</th></tr>
                </thead>
                <tbody>
                  {billingData.map((invoice) => (
                    <tr key={invoice.id || invoice._id || invoice.invoiceId}>
                      <td><div className="invoice-id"><i className="bi bi-file-earmark-pdf"></i>{invoice.id || invoice._id || invoice.invoiceId}</div></td>
                      <td>{invoice.date || invoice.createdAt || invoice.paidAt}</td>
                      <td>{invoice.plan || invoice.planName || invoice.description}</td>
                      <td>{/* formatMoney omitted here for brevity */}{invoice.amount}</td>
                      <td><span style={{fontSize: '14px'}} className={`status-badge ${(invoice.status || invoice.paymentStatus || "unknown").toLowerCase()}`}>{(invoice.status || invoice.paymentStatus || "Unknown").toString()}</span></td>
                      <td>{invoice.invoiceUrl ? <a href={invoice.invoiceUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-download" title="Open invoice"><i className="bi bi-download"></i></a> : <button className="btn btn-sm btn-download" onClick={() => handleDownloadInvoice(invoice.id || invoice._id || invoice.invoiceId)} title="Download invoice"><i className="bi bi-download"></i></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="no-invoices"><p>No invoices found</p></div>}
        </div>
      )}

      {/* Add card modal */}
      {showAddCardModal && (
        <div className="modal-overlay">
          <div className="modal-content add-card-modal">
            <div className="modal-header">
              <h5>Add Card</h5>
              <button className="btn-close" onClick={() => setShowAddCardModal(false)}></button>
            </div>
            <div className="modal-body">
              <Elements stripe={stripePromise}>
                <AddCardForm
                  onSuccess={handleAddCardSubmit}
                  onCancel={() => setShowAddCardModal(false)}
                  loading={paymentLoading}
                  setLoading={setPaymentLoading}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}

      {showCardDetailsModal && cardDetails && (
        <div className="modal-overlay">
          <div className="modal-content card-details-modal" style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h5>Card Details</h5>
              <button className="btn-close" onClick={() => setShowCardDetailsModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="virtual-card-container">
                <div className={`virtual-card brand-${(cardDetails.brand || 'default').toLowerCase()}`}>
                  <div className="virtual-card-header">
                    <span className="virtual-card-bank">MyWorth Secure</span>
                    <span className="virtual-card-brand-name">{cardDetails.brand || 'Card'}</span>
                  </div>
                  <div className="virtual-card-middle">
                    <div className="virtual-card-chip"></div>
                  </div>
                  <div className="virtual-card-number">
                    ••••  ••••  ••••  {cardDetails.last4 || '----'}
                  </div>
                  <div className="virtual-card-footer">
                    <div className="virtual-card-info-group">
                      <span className="virtual-card-label">Card Holder</span>
                      <span className="virtual-card-value">{cardDetails.name || 'Valued Customer'}</span>
                    </div>
                    <div className="virtual-card-info-group">
                      <span className="virtual-card-label">Expires</span>
                      <span className="virtual-card-value">
                        {String(cardDetails.expiryMonth || '00').padStart(2, '0')} / {String(cardDetails.expiryYear || '00').slice(-2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="card-details-content mt-3">
                <div className="detail-row">
                  <span className="form-label text-muted">Brand</span>
                  <span className="detail-value text-capitalize">{cardDetails.brand || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="form-label text-muted">Status</span>
                  <span className="detail-value">
                    {cardDetails.default ? (
                      <span className="badge bg-success-subtle text-success border border-success-subtle">Default Payment Card</span>
                    ) : (
                      <span className="badge bg-light text-secondary border">Backup Card</span>
                    )}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="form-label text-muted">CVV Status</span>
                  <span className="detail-value text-success">Secured (XXX)</span>
                </div>
              </div>

              <div className="modal-actions mt-4">
                <button type="button" className="btn btn-secondary w-100" onClick={() => setShowCardDetailsModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Card Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header">
              <h3>Delete Card</h3>
              <button className="btn-close" onClick={() => { setShowDeleteConfirmModal(false); setCardToDelete(null); }}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this card? This action cannot be undone.</p>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowDeleteConfirmModal(false); setCardToDelete(null); }}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={() => { handleDeleteCard(cardToDelete); setShowDeleteConfirmModal(false); setCardToDelete(null); }}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Downgrade Warning Modal */}
      {downgradeModal.show && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header">
              <h3>⚠️ Downgrade Plan</h3>
              <button className="btn-close" onClick={() => setDowngradeModal((s) => ({ ...s, show: false }))}></button>
            </div>
            <div className="modal-body">
              <p>You currently have <strong>{downgradeModal.currentAssetCount}</strong> active digital assets.</p>
              <p>Your new plan allows <strong>{downgradeModal.newPlanLimit === 'Unlimited' ? 'Unlimited' : downgradeModal.newPlanLimit}</strong> active digital assets.</p>
              {downgradeModal.assetsToArchive > 0 ? (
                <div className="alert alert-warning mt-2">
                  <i className="bi bi-archive me-2"></i>
                  All <strong>{downgradeModal.currentAssetCount}</strong> digital assets will be archived.
                  You can manually reactivate up to <strong>{downgradeModal.newPlanLimit}</strong> from the Archived tab.
                </div>
              ) : (
                <div className="alert alert-success mt-2">
                  <i className="bi bi-check-circle me-2"></i>
                  You are within the new plan limit. No assets will be archived.
                </div>
              )}
              <div className="alert alert-info mt-2" style={{ fontSize: '13px' }}>
                <i className="bi bi-info-circle me-2"></i>
                Physical assets are <strong>unlimited</strong> on all plans and will not be affected.
              </div>
              <p className="text-muted mt-2" style={{ fontSize: '13px' }}>Premium features will no longer be available after downgrade.</p>
              <div className="modal-actions mt-3">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDowngradeModal((s) => ({ ...s, show: false }))}
                  disabled={changeFlow.loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmChangePlan}
                  disabled={changeFlow.loading}
                >
                  {changeFlow.loading ? 'Downgrading...' : 'Confirm Downgrade'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stripe subscription modal */}
{showPaymentModal && selectedPlan && (
  <SubscriptionModal
    selectedPlan={selectedPlan}
    paymentIntentPayload={paymentIntentPayload}
    onClose={() => {
      setShowPaymentModal(false)
      setUseNewCardInModal(false)
    }}
    plansDetails={plansDetails}
      isFreeSubscription={treatAsNew}
    mySubscription={mySubscription}
    selectedCard={selectedCard}
    useNewCardInModal={useNewCardInModal}
    setUseNewCardInModal={setUseNewCardInModal}
    cards={cards}
    onCreated={async (data) => {
      // feedback
      showSuccessToast("Subscription created!")

      // close modal
      setShowPaymentModal(false)

      // refresh plans if parent provides refreshPlans()
      if (typeof refreshPlans === "function") {
        try { await refreshPlans() } catch (e) { console.warn(e) }
      }

      // prefer server returned plan id; fallback to selectedPlan
      const newPlanId = data?.planId || data?.subscription?.planId || selectedPlan?.id

      if (newPlanId) {
        setCurrentPlan(String(newPlanId))     // mark as current
        setActiveTab("plan")                 // switch to Plan tab
        setSelectedPlan(selectedPlan)        // keep the plan selected so right pane shows details

        // optional: scroll selected plan into view
        setTimeout(() => {
          try {
            const el = document.querySelector('.plan-card.active')
            if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          } catch (e) {}
        }, 150)
      } else {
        setActiveTab("plan")
      }

      // refresh "my subscription" (your API) so UI shows latest subscription
      try {
        await fetchMySubscription()
      } catch (e) {
        console.warn("fetchMySubscription failed after creating subscription:", e)
      }

      // Reactivate archived assets after upgrade — respects new plan limit
      const newDigitalLimit = selectedPlan?.limits?.digitalAssets?.total ?? selectedPlan?.limits?.digitalAssets ?? -1
      const reactivatedCount = await reactivateAllArchivedAssets(newDigitalLimit)
      if (reactivatedCount > 0) {
        showSuccessToast('Archived assets have been reactivated!')
      } else {
        showSuccessToast('Plan upgraded successfully')
      }
    }}
  />
)}

    </div>
  )
}

export default SubscriptionManagement
